import { useParallaxTransform } from '@/hooks/useParallax';
import { useState, useEffect, useRef } from 'react';
import SectionTitle from '@/components/base/SectionTitle';

const profiles = [
  {
    name: 'Lou Matthey',
    role: 'Co-fondatrice',
    image: 'https://storage.readdy-site.link/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/27c8589e-baf1-48ab-9097-afede52b9f32_Lou.png?v=24d20ea36afab7e1d8c2d3aef0fb169f',
  },
  {
    name: 'Lionel Mertes',
    role: 'Co-fondateur',
    image: 'https://storage.readdy-site.link/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/1b897476-f40c-4f39-a9f3-9ba867c334cf_Lionel.png?v=7b684914143f084be008fd3388c78c3a',
  },
];

function useCountUp(end: number, duration = 2000) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          let start = 0;
          let lastVal = 0;
          const step = (timestamp: number) => {
            if (!start) start = timestamp;
            const progress = Math.min((timestamp - start) / duration, 1);
            const val = Math.floor(progress * end);
            if (val !== lastVal) {
              lastVal = val;
              setCount(val);
            }
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);

  return { count, ref };
}

export default function About() {
  const profileRef = useParallaxTransform(0.04);
  const { count, ref: counterRef } = useCountUp(10, 1500);

  return (
    <section id="apropos" className="py-20 sm:py-28 lg:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        <SectionTitle
          label="À Propos"
          title="À Propos"
          delay={100}
        />

        <p className="text-xl sm:text-2xl lg:text-3xl font-medium italic text-[#00A8B5] max-w-4xl mx-auto leading-relaxed text-center mb-14 sm:mb-16 lg:mb-8 animate-fade-in-up delay-300">
          Nous avons conçu cette remorque comme la solution que nous aurions toujours voulu avoir sur nos propres événements.
        </p>

        {/* Profils côte à côte, épurés */}
        <div ref={profileRef} className="relative rounded-2xl border border-gray-200 bg-white/70 overflow-hidden mb-14 sm:mb-16 lg:mb-8 will-change-transform animate-fade-in-up delay-500 transition-all duration-[250ms] ease-out hover:-translate-y-1 hover:shadow-lg hover:scale-[1.01]">
          {/* Logo watermark centré entre les deux profils */}
          <img
            src="https://storage.readdy-site.link/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/ad65f5a4-f716-427f-a61e-29f4980b7d5c_transparent.png?v=239985b6871b0c84f08fbccdaabe0b84"
            alt="PLUG and BAR LM"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 sm:w-[28rem] sm:h-[28rem] lg:w-[28rem] lg:h-[28rem] object-contain z-10"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 relative z-20">
            {profiles.map((profile) => (
              <div
                key={profile.name}
                className="flex flex-col items-center text-center p-8 sm:p-10"
              >
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-[#00A8B5]/20 mb-5 flex-shrink-0">
                  <img
                    src={profile.image}
                    alt={profile.name}
                    className="w-full h-full object-cover object-center scale-[1.15]"
                  />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-1">{profile.name}</h4>
                <p className="text-[#00A8B5] text-sm font-semibold uppercase tracking-wider">{profile.role}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bloc commun sous les profils */}
        <div className="max-w-4xl mx-auto animate-fade-in-up delay-600">
          <div className="rounded-2xl border border-gray-200 bg-white/80 px-8 py-10 sm:px-12 sm:py-12 lg:py-14 transition-all duration-[250ms] ease-out hover:-translate-y-1 hover:shadow-lg hover:scale-[1.01]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 text-center">
              {/* Expérience */}
              <div ref={counterRef} className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-[#00A8B5]/10 flex items-center justify-center mb-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-briefcase-line text-[#00A8B5] text-2xl"></i>
                  </div>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl sm:text-5xl font-bold text-[#00A8B5]">{count}</span>
                  <span className="text-2xl sm:text-3xl font-bold text-[#00A8B5]">+</span>
                </div>
                <p className="text-gray-600 text-sm sm:text-base">Années d&apos;expérience dans l&apos;événementiel</p>
              </div>

              {/* Adresse */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-[#00A8B5]/10 flex items-center justify-center mb-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-[#00A8B5] text-2xl"></i>
                  </div>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-1">LM Experience Sàrl</h4>
                <p className="text-gray-600 text-sm sm:text-base">Rue de L&apos;Hôtel-de-Ville 2<br />1800 Vevey</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}